/**
 * useSortState — shared sort-key + sort-dir + click-handler hook for
 * analytics-v2 tables. Extracted from CampaignsWidget + MerchantsWidget
 * (CORE-960 R1) once the pattern reached three copies.
 *
 * Convention (load-bearing across widgets):
 *   - Same key clicked → toggle direction.
 *   - New key clicked → jump to that key at `desc` (highest-first, matches
 *     the backend's primary sort direction).
 *
 * A widget with multiple tables calls this once per table; each instance
 * has independent state.
 */

import { useState } from 'react';
import type { SortDir } from '@/utils/sort';

export interface SortState<K extends string> {
  sortKey: K;
  sortDir: SortDir;
  handleSortClick: (key: K) => void;
}

export function useSortState<K extends string>(
  defaultKey: K,
  defaultDir: SortDir,
): SortState<K> {
  const [sortKey, setSortKey] = useState<K>(defaultKey);
  const [sortDir, setSortDir] = useState<SortDir>(defaultDir);

  // Compute next state from the currently-committed sortKey and call both
  // setters at the top level. Calling setSortDir inside a setSortKey updater
  // violates React's updater-purity contract — StrictMode double-invokes
  // updaters in dev, which would queue the toggle twice and net to no change
  // on same-key clicks. Depending on `sortKey` in the closure is fine: click
  // handlers aren't in a hot path and downstream doesn't memoise on identity.
  function handleSortClick(key: K): void {
    if (key === sortKey) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
      return;
    }
    setSortKey(key);
    setSortDir('desc');
  }

  return { sortKey, sortDir, handleSortClick };
}
